import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getApiClient } from '@signalvote/api-client';
import type {
  ShiftDto,
  ShiftListItemDto,
  ShiftListParams,
  CreateShiftRequest,
  UpdateShiftRequest,
  CompleteShiftRequest,
} from '@signalvote/shared';
import { queryKeys } from '@/lib/queryKeys';
import { useCampaignStore } from '@/stores';

const api = getApiClient();

/**
 * Fetch shifts with optional filtering
 */
export function useShifts(params?: ShiftListParams) {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.shifts.list(params),
    queryFn: async () => {
      const response = await api.shifts.getShifts(params);
      return response;
    },
    enabled: !!selectedCampaign,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Fetch a single shift by ID
 */
export function useShift(id: string | undefined) {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.shifts.detail(id!),
    queryFn: () => api.shifts.getShift(id!),
    enabled: !!id && !!selectedCampaign,
    staleTime: 30 * 1000,
  });
}

/**
 * Create a new shift
 */
export function useCreateShift() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateShiftRequest) => api.shifts.createShift(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.shifts.all });
    },
  });
}

/**
 * Update an existing shift
 */
export function useUpdateShift() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateShiftRequest }) =>
      api.shifts.updateShift(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.shifts.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.shifts.detail(id) });
    },
  });
}

/**
 * Delete a shift
 */
export function useDeleteShift() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.shifts.deleteShift(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.shifts.all });
    },
  });
}

/**
 * Claim an available shift
 */
export function useClaimShift() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.shifts.claimShift(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.shifts.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.shifts.detail(id) });
    },
  });
}

/**
 * Release a claimed shift (before starting)
 */
export function useReleaseShift() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.shifts.releaseShift(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.shifts.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.shifts.detail(id) });
    },
  });
}

/**
 * Start a claimed shift
 */
export function useStartShift() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.shifts.startShift(id, null),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.shifts.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.shifts.detail(id) });
    },
  });
}

/**
 * Complete a shift with optional stats
 */
export function useCompleteShift() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CompleteShiftRequest }) =>
      api.shifts.completeShift(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.shifts.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.shifts.detail(id) });
    },
  });
}
